/** Pure logic for the MA edit-and-sign console (SPEC ma-editable-peri-registry).
 *  Kept out of the component so the change-set, FSH monotonicity guard, and
 *  readiness gate are unit-testable. */
import type { EditableCell, EditItem } from './consoleApi';

export type CellValue = string | number;

/** Baseline map path → current server value, coerced to the widget's type. */
export function baselineValues(cells: EditableCell[]): Record<string, CellValue> {
  const out: Record<string, CellValue> = {};
  for (const c of cells) {
    out[c.path] = c.kind === 'number' ? Number(c.value) : String(c.value ?? '');
  }
  return out;
}

export type Change = { path: string; label: string; from: CellValue; to: CellValue; kind: EditableCell['kind'] };

/** Cells whose current value differs from the server baseline. */
export function computeChanges(cells: EditableCell[], values: Record<string, CellValue>): Change[] {
  const base = baselineValues(cells);
  const out: Change[] = [];
  for (const c of cells) {
    if (c.locked) continue;
    const cur = values[c.path];
    if (cur === undefined) continue;
    const from = base[c.path] ?? '';
    if (String(cur) !== String(from)) {
      out.push({ path: c.path, label: c.label, from, to: cur, kind: c.kind });
    }
  }
  return out;
}

export function toEdits(changes: Change[]): EditItem[] {
  return changes.map((c) => ({ path: c.path, value: c.kind === 'number' ? Number(c.to) : c.to }));
}

/** FSH cutpoint maxima must be strictly increasing within a band. Returns a
 *  human message if violated (blocks submit), else null. Mirrors the server
 *  contiguity/monotonicity invariant so the MA sees the problem before signing. */
export function fshMonotonicityError(
  cells: EditableCell[], values: Record<string, CellValue>,
): string | null {
  // FSH max cells in category order (server emits them ordered). v6: a single
  // flat `thresholds` list — age-free, no per-band grouping.
  const seq: { label: string; value: number }[] = [];
  for (const c of cells) {
    const m = /^fsh_categories\.thresholds\.([^.]+)\.max$/.exec(c.path);
    if (!m || !m[1]) continue;
    seq.push({ label: m[1], value: Number(values[c.path]) });
  }
  for (let i = 1; i < seq.length; i += 1) {
    const prev = seq[i - 1];
    const cur = seq[i];
    if (!prev || !cur) continue;
    if (!(cur.value > prev.value)) {
      return `FSH cutpoints must strictly increase: ${prev.label} (${prev.value}) must be below ${cur.label} (${cur.value}).`;
    }
  }
  return null;
}

/** Editable cells still carrying an unsigned-required value (unchanged from the
 *  MA_REQUIRED placeholder). These must be authored before an approve. */
export function unsignedRequired(
  cells: EditableCell[], values: Record<string, CellValue>,
): EditableCell[] {
  const base = baselineValues(cells);
  return cells.filter((c) => c.required && !c.locked && String(values[c.path]) === String(base[c.path]));
}

export type Readiness =
  | { ready: true }
  | { ready: false; reason: string };

/** Can the MA submit? request_changes only needs a note; approve needs a
 *  signatory, no FSH violation, and every required cell authored. */
export function canSubmit(args: {
  decision: 'approve' | 'request_changes';
  attestedSignatory: string;
  note: string;
  fshError: string | null;
  unsignedRequiredCount: number;
}): Readiness {
  if (args.attestedSignatory.trim().length < 3) {
    return { ready: false, reason: 'Enter your name and credentials to sign.' };
  }
  if (args.decision === 'request_changes') {
    return args.note.trim().length > 0
      ? { ready: true }
      : { ready: false, reason: 'A note is required to request changes.' };
  }
  if (args.fshError) return { ready: false, reason: args.fshError };
  if (args.unsignedRequiredCount > 0) {
    return { ready: false, reason: `${args.unsignedRequiredCount} required value still unsigned.` };
  }
  return { ready: true };
}
